import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { WhatsAppTemplate } from '../../types/whatsappTemplate';

interface WhatsAppTemplateState {
  templates: WhatsAppTemplate[];
  loading: boolean;
  error: string | null;
}

// Default templates for each stage
const defaultTemplates: Omit<WhatsAppTemplate, 'id' | 'createdAt' | 'updatedAt'>[] = [
  {
    stage: 'enquiry',
    title: 'Welcome Message',
    message: 'Hello! Thank you for your enquiry. We are excited to help you with your project. How can we assist you today?',
  },
  {
    stage: 'contacted',
    title: 'Initial Contact',
    message: 'Hello! We have received your contact information. Our team will reach out to you shortly to discuss your requirements.',
  },
  {
    stage: 'followup',
    title: 'Follow-up Message',
    message: 'Hello! Just following up on our previous conversation. Please let us know if you have any questions or need further assistance.',
  },
  {
    stage: 'storeVisit',
    title: 'Store Visit Reminder',
    message: 'Hello! This is a reminder about your scheduled store visit. We look forward to meeting you and discussing your project in detail.',
  },
  {
    stage: 'designFinalized',
    title: 'Design Confirmation',
    message: 'Hello! We are pleased to confirm that your design has been finalized. We will proceed with the next steps shortly.',
  },
  {
    stage: 'siteVisit',
    title: 'Site Visit Reminder',
    message: 'Hello! This is a reminder about your scheduled site visit. Our team will be there to take measurements and assess the site.',
  },
  {
    stage: 'measurement',
    title: 'Measurement Update',
    message: 'Hello! We have completed the site measurement. We will prepare the quotation based on the measurements taken.',
  },
  {
    stage: 'quotation',
    title: 'Quotation Sent',
    message: 'Hello! We have sent you the quotation. Please review it and let us know if you have any questions or would like to proceed.',
  },
  {
    stage: 'wonLostHold',
    title: 'Thank You Message',
    message: 'Hello! Thank you for choosing us. We are committed to providing you with the best service and quality.',
  },
];

const initialState: WhatsAppTemplateState = {
  templates: defaultTemplates.map((template, index) => ({
    ...template,
    id: `template-${index + 1}`,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  })),
  loading: false,
  error: null,
};

const whatsappTemplateSlice = createSlice({
  name: 'whatsappTemplates',
  initialState,
  reducers: {
    addTemplate: (state, action: PayloadAction<Omit<WhatsAppTemplate, 'id' | 'createdAt' | 'updatedAt'>>) => {
      const newTemplate: WhatsAppTemplate = {
        ...action.payload,
        id: `template-${Date.now()}`,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      // Add new templates to the beginning so they appear at the top of the list
      state.templates.unshift(newTemplate);
    },
    updateTemplate: (state, action: PayloadAction<WhatsAppTemplate>) => {
      const index = state.templates.findIndex((t) => t.id === action.payload.id);
      if (index !== -1) {
        state.templates[index] = {
          ...action.payload,
          updatedAt: new Date().toISOString(),
        };
      }
    },
    deleteTemplate: (state, action: PayloadAction<string>) => {
      state.templates = state.templates.filter((t) => t.id !== action.payload);
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
  },
});

export const { addTemplate, updateTemplate, deleteTemplate, setLoading, setError } =
  whatsappTemplateSlice.actions;
export default whatsappTemplateSlice.reducer;

